var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Notification API client.
 *
 * REST: thin wrappers over the existing axios instance.
 * SSE : `@microsoft/fetch-event-source` so we can carry the Bearer JWT
 *       (the native EventSource API cannot set custom headers).
 */
import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source';
import { axiosInstance, baseURL } from './axiosInstance';
import { tokenService } from '../auth/tokenService';
// ─── REST helpers ─────────────────────────────────────────────────────────────
export const notificationsApi = {
    list(limit = 50) {
        return axiosInstance
            .get('/notifications', { params: { limit } })
            .then((r) => r.data);
    },
    unreadCount() {
        return axiosInstance
            .get('/notifications/unread-count')
            .then((r) => r.data);
    },
    markRead(id) {
        return axiosInstance
            .post(`/notifications/${id}/read`)
            .then((r) => r.data);
    },
    markAllRead() {
        return axiosInstance
            .post('/notifications/read-all')
            .then((r) => r.data);
    },
    /**
     * Hard-delete every notification for the current user (read + unread).
     * Backend cascades the outbox rows, so a single call clears the bell badge
     * and the dashboard notifications panel in one round-trip.
     */
    deleteAll() {
        return axiosInstance
            .delete('/notifications')
            .then((r) => r.data);
    },
};
/**
 * Open a long-lived SSE connection to `/notifications/stream`.
 * Returns an `abort()` function that the caller MUST invoke on cleanup
 * (component unmount / user logout) to close the underlying connection.
 */
export function openNotificationsStream({ onMessage, onOpen, onError }, getLastEventId) {
    const controller = new AbortController();
    let authRefreshInFlight = null;
    // Fire-and-forget — fetchEventSource manages its own retry loop.
    void fetchEventSource(`${baseURL}/notifications/stream`, {
        signal: controller.signal,
        // Re-resolve the bearer token on every (re)connect so refresh works.
        fetch: (input, init) => __awaiter(this, void 0, void 0, function* () {
            const token = yield tokenService.getAccessToken().catch(() => null);
            const headers = new Headers((init === null || init === void 0 ? void 0 : init.headers) || {});
            if (token)
                headers.set('Authorization', `Bearer ${token}`);
            const lastId = getLastEventId();
            if (lastId)
                headers.set('Last-Event-ID', lastId);
            headers.set('Accept', 'text/event-stream');
            return fetch(input, Object.assign(Object.assign({}, init), { headers, credentials: 'include' }));
        }),
        openWhenHidden: true, // keep stream alive when tab is backgrounded
        onopen(response) {
            return __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (response.ok && ((_a = response.headers.get('content-type')) === null || _a === void 0 ? void 0 : _a.includes(EventStreamContentType))) {
                    onOpen === null || onOpen === void 0 ? void 0 : onOpen();
                    return;
                }
                // Bubble auth failures to onerror for refresh-and-retry recovery.
                if (response.status === 401 || response.status === 403) {
                    throw new Error(`SSE_AUTH_${response.status}`);
                }
                throw new Error(`SSE_BAD_STATUS_${response.status}`);
            });
        },
        onmessage(ev) {
            if (!ev.data)
                return;
            // Typed control frames from the server. `event: shutdown` is emitted
            // by the backend during graceful shutdown (rolling pod restart,
            // --reload, SIGTERM) so we know it's not a real notification \u2014 don't
            // try to JSON.parse the payload. fetch-event-source will auto-reconnect
            // on the next dropped frame, replaying via Last-Event-ID so no
            // notifications are lost.
            if (ev.event === 'shutdown') {
                return;
            }
            try {
                const parsed = JSON.parse(ev.data);
                onMessage(parsed);
            }
            catch (err) {
                console.warn('[notifications] failed to parse SSE message', err);
            }
        },
        onerror(err) {
            onError === null || onError === void 0 ? void 0 : onError(err);
            // Recover auth errors by refreshing the JWT and reconnecting.
            // This avoids "stream dies until full page refresh" when a token
            // expires or the first connect races auth bootstrap.
            if (err instanceof Error && err.message.startsWith('SSE_AUTH_')) {
                if (!authRefreshInFlight) {
                    // Kick off the refresh; the 1 000 ms retry delay gives it time to
                    // complete before fetchEventSource re-opens the connection.
                    authRefreshInFlight = tokenService.refreshToken().finally(() => {
                        authRefreshInFlight = null;
                    });
                }
                return 1000;
            }
            // Otherwise let fetchEventSource back-off and reconnect.
            return 5000;
        },
    }).catch((err) => {
        onError === null || onError === void 0 ? void 0 : onError(err);
    });
    return () => controller.abort();
}
